import { Card, CardBody, Icon } from '@repo/ui';
import type { ReportCard } from '../../../types';

interface ReportCardComponentProps {
  report: ReportCard;
  onReportClick: (report: ReportCard) => void;
}

function getCategoryIconBg(category: string): string {
  const bgMap: Record<string, string> = {
    Billing: 'bg-blue-100 dark:bg-blue-900/20',
    Revenue: 'bg-green-100 dark:bg-green-900/20',
    Productivity: 'bg-purple-100 dark:bg-purple-900/20',
  };
  return bgMap[category] || 'bg-gray-100 dark:bg-gray-900/20';
}

function getCategoryIconColor(category: string): string {
  const colorMap: Record<string, string> = {
    Billing: 'text-blue-600 dark:text-blue-400',
    Revenue: 'text-green-600 dark:text-green-400',
    Productivity: 'text-purple-600 dark:text-purple-400',
  };
  return colorMap[category] || 'text-gray-600 dark:text-gray-400';
}

function getCategoryBadgeStyles(category: string): string {
  const badgeMap: Record<string, string> = {
    Billing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
    Revenue: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
    Productivity: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
  };
  return badgeMap[category] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
}

export function ReportCardComponent({ report, onReportClick }: ReportCardComponentProps) {
  return (
    <Card
      variant="outlined"
      padding="none"
      className={`
        transition-all duration-200 cursor-pointer group
        ${report.isComingSoon ? 'opacity-60 cursor-not-allowed' : 'hover:-translate-y-0.5'}
      `}
      onClick={() => onReportClick(report)}
    >
      <CardBody className="p-6">
        <div className="flex items-start space-x-4">
          {/* Icon */}
          <div className={`flex-shrink-0 p-2 rounded-lg ${getCategoryIconBg(report.category)}`}>
            <Icon name={report.icon} className={`h-6 w-6 ${getCategoryIconColor(report.category)}`} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-medium text-xtnd-dark dark:text-xtnd-white group-hover:text-xtnd-blue transition-colors">
                {report.title}
              </h3>
              {report.isComingSoon ? (
                <span className="text-xs px-2 py-1 bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400 rounded-full">
                  Coming Soon
                </span>
              ) : (
                <Icon
                  name="HiArrowRight"
                  className="h-4 w-4 text-gray-400 group-hover:text-xtnd-blue transition-colors"
                />
              )}
            </div>

            <p
              className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {report.description}
            </p>

            {/* Category Badge */}
            <div className="mt-3">
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryBadgeStyles(report.category)}`}
              >
                {report.category} report
              </span>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

